import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ModalPanel } from '@els/els-react--modal-panel';
import { Card } from '@els/els-react--card';
import { ButtonWithIcon } from '@els/els-react--button';
import InnerHTML from '@Components/InnerHTML';
import RxStructureService from '@Services/RxStructureService';
import SubstanceProperty from '@Components/Results/substances/SubstanceProperty';
import { getFactCount } from '@Components/Results/substances/utils/factCountHelper';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import NumberFormatter from '@Utils/NumberFormatter';
import Slugify from '@Utils/Slugify';

import './SubstanceHitDataModal.scss';

function SubstanceHitDataModal({ metadata, substance, database, fact, onClose }) {
  const { index: substanceIndex, details } = substance;
  const name = substance.name || details?.chemicalNames?.[0] || `${database?.name || ''} ID: ${details?.id || ''}`.trim();
  const formula = details?.linearFormula || details?.formula;

  const heading = useMemo(() => {
    const count = getFactCount(fact, substance);
    const title = fact.title || 'Hit Data';
    return count ? `${title} - ${NumberFormatter(count, 0)}` : title;
  }, [fact, substance]);

  const headingId = `substance-hitdata-modal-heading-${Slugify(fact.id || fact.title || 'hit-data')}-${substanceIndex}`;

  const onViewDetails = () => {
    RxStructureService.viewDetails(metadata?.id, details?.id, substance.databaseId);
    onClose();
  };

  return (
    <ModalPanel
      className="substance-hit-data-modal"
      panelPosition="right"
      modalProps={{ onClose, label: name, shouldHideCloseButton: true }}>
      <div className="substance-hit-data-modal__content" data-e2e="substance-hit-data-modal">
        <div className="substance-hit-data-modal__header">
          <InnerHTML
            tag="h2"
            mode="escapeCustom"
            className="substance-hit-data-modal__title u-els-font-size-h2 u-els-line-height-default"
            html={name}
          />
          <ButtonWithIcon
            data-e2e="substancehitdata-modal-close"
            className="substance-hit-data-modal__close"
            type="borderless"
            size="small"
            sprite="Close"
            iconRight
            iconOnly
            aria-label={`Close ${name}`}
            onClick={onClose}>
            Close {name}
          </ButtonWithIcon>
        </div>

        {formula && <InnerHTML tag="div" mode="escape" className="substance-hit-data-modal__formula u-els-font-size-base" html={formula} />}

        <div className="substances-hit-details substances-hit-details__container">
          <Card
            className="substances-hit-details__card"
            heading={
              <p id={headingId} className="substances-hit-details__title">
                {heading}
              </p>
            }
            headingLevel="h3"
            role="region"
            aria-labelledby={headingId}>
            <SubstanceProperty
              metadata={metadata}
              fact={fact}
              details={details}
              database={database}
              substanceIndex={substanceIndex}
              substance={substance}
              isTabPanel
            />
          </Card>
        </div>

        <ButtonWithIcon
          data-e2e="substance-hit-data-modal-view-details"
          className="substance-hit-data-modal__view-details"
          type="link"
          size="small"
          sprite="ChevronRight"
          aria-label={`View substance details for ${name}`}
          onClick={onViewDetails}>
          View substance details
        </ButtonWithIcon>
      </div>
    </ModalPanel>
  );
}

SubstanceHitDataModal.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  database: PropTypes.object.isRequired,
  fact: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SubstanceHitDataModal;
